/**
 * CalendarPage — unified Calendar view for Outpost CRM.
 * Merged "Book Meeting" + "Calendar" into one screen.
 *
 * Features:
 *  - Month/Week/Day view toggle (Google Calendar mirror)
 *  - "Schedule Meeting" modal with CRM contact search + GCal creation
 *  - Admin settings panel (slides in from right)
 *  - Real-time sync with drew@outpostprep.com Google Calendar
 */

import { useState, useEffect, useCallback, useMemo } from "react";

// ── Brand tokens (light theme — matches App.js) ─────────────────────────────
const C = {
  green: "#494C43",       // sidebar only
  greenDark: "#494C43",   // sidebar
  greenDeep: "#F5F5F0",   // page bg + input bg
  orange: "#F97316",      // primary accent (orange-500)
  orangeHover: "#EA580C", // orange-600
  orangeLight: "#FFF7ED", // orange-50 hover bg
  white: "#FFFFFF",
  grayMid: "#64748B",     // slate-500 (secondary text)
  grayDark: "#334155",    // slate-700
  cardBg: "#FFFFFF",      // white cards
  cardBorder: "#E2E8F0",  // slate-200
  text: "#1E293B",        // slate-800
  textLight: "#475569",   // slate-600
  success: "#10B981",     // emerald-500
  warning: "#F59E0B",     // amber-500
  danger: "#EF4444",      // red-500
};

const CRM_URL = "https://outpost-crm-production.up.railway.app";

async function api(path, method = "GET", body = null) {
  try {
    const opts = { method, headers: { "Content-Type": "application/json" } };
    if (body) opts.body = JSON.stringify(body);
    const r = await fetch(`${CRM_URL}${path}`, opts);
    if (!r.ok) throw new Error(`${r.status}`);
    return await r.json();
  } catch (e) { console.error("Calendar API error:", path, e.message); return null; }
}

// ── Date helpers ─────────────────────────────────────────────────────────────
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
// eslint-disable-next-line no-unused-vars
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function startOfWeek(d) { const s = new Date(d); s.setDate(s.getDate() - s.getDay()); return s; }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function isSameDay(a, b) { return a.toDateString() === b.toDateString(); }
function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
function fmtDateShort(d) {
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
function fmtDateFull(d) {
  return d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

const HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 6am–9pm

// ── Event color by type keyword ──────────────────────────────────────────────
function eventColor(ev) {
  const t = ((ev.title || "") + " " + (ev.description || "")).toLowerCase();
  if (t.includes("meeting") || t.includes("call") || t.includes("demo")) return "#0D9488"; // teal
  if (t.includes("task") || t.includes("follow")) return C.orange;
  if (t.includes("personal") || t.includes("block")) return C.grayDark;
  return "#3B82F6"; // blue default
}

// ── Styles (light theme, orange accents) ─────────────────────────────────────
const btn = (variant) => ({
  border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontFamily: "inherit",
  transition: "all 0.15s", fontSize: 13,
  ...(variant === "primary"
    ? { background: C.orange, color: "#FFFFFF", padding: "8px 16px" }
    : { background: C.white, color: C.grayDark, border: `1px solid ${C.cardBorder}`, padding: "6px 12px" }),
});
const input = {
  background: "#F8FAFC", border: `1px solid ${C.cardBorder}`, borderRadius: 6,
  color: C.text, padding: "8px 10px", fontSize: 13, fontFamily: "inherit", outline: "none", width: "100%",
};
const select = { ...input, cursor: "pointer" };

// ═════════════════════════════════════════════════════════════════════════════
// CalendarPage — the main export
// ═════════════════════════════════════════════════════════════════════════════
export default function CalendarPage() {
  const [view, setView] = useState("month"); // month | week | day
  const [focus, setFocus] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);

  const rangeStart = useMemo(() => {
    if (view === "month") return startOfWeek(startOfMonth(focus));
    if (view === "week") return startOfWeek(focus);
    return new Date(focus.getFullYear(), focus.getMonth(), focus.getDate());
  }, [view, focus]);

  const rangeEnd = useMemo(() => {
    if (view === "month") return addDays(startOfWeek(startOfMonth(focus)), 42);
    if (view === "week") return addDays(startOfWeek(focus), 7);
    return addDays(focus, 1);
  }, [view, focus]);

  const fetchEvents = useCallback(() => {
    setLoading(true);
    const s = rangeStart.toISOString();
    const e = rangeEnd.toISOString();
    api(`/calendar/events?start=${s}&end=${e}`).then(r => {
      setEvents(Array.isArray(r) ? r : []);
      setLoading(false);
    });
  }, [rangeStart, rangeEnd]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  function nav(dir) {
    setFocus(f => {
      const d = new Date(f);
      if (view === "month") d.setMonth(d.getMonth() + dir);
      else if (view === "week") d.setDate(d.getDate() + 7 * dir);
      else d.setDate(d.getDate() + dir);
      return d;
    });
  }

  const today = new Date();

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button onClick={() => nav(-1)} style={btn("ghost")}>&lsaquo;</button>
          <button onClick={() => setFocus(new Date())} style={btn("ghost")}>Today</button>
          <button onClick={() => nav(1)} style={btn("ghost")}>&rsaquo;</button>
          <div style={{ fontSize: 16, fontWeight: 700, marginLeft: 8 }}>
            {view === "day" ? fmtDateFull(focus) : focus.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {["month", "week", "day"].map(v => (
            <button key={v} onClick={() => setView(v)}
              style={{ ...btn(v === view ? "primary" : "ghost"), fontSize: 12, padding: "5px 12px",
                textTransform: "capitalize" }}>{v}</button>
          ))}
          <button onClick={() => setShowSchedule(true)} style={{ ...btn("primary"), fontSize: 12 }}>
            + Schedule Meeting
          </button>
          <button onClick={() => setShowAdmin(a => !a)} style={{ ...btn("ghost"), fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 15 }}>&#9881;</span> Availability Settings
          </button>
        </div>
      </div>

      {/* Calendar body */}
      <div style={{ flex: 1, overflow: "auto", position: "relative" }}>
        {loading && <div style={{ textAlign: "center", padding: 40, color: C.grayMid }}>Loading calendar...</div>}
        {!loading && view === "month" && (
          <MonthGrid focus={focus} events={events} today={today}
            onSelectDay={d => { setFocus(d); setView("day"); }}
            onSelectEvent={setSelected} />
        )}
        {!loading && view === "week" && (
          <WeekGrid focus={focus} events={events} today={today} onSelectEvent={setSelected} />
        )}
        {!loading && view === "day" && (
          <DayGrid focus={focus} events={events} today={today} onSelectEvent={setSelected} />
        )}
      </div>

      {/* Event detail panel */}
      {selected && <EventDetail event={selected} onClose={() => setSelected(null)} onCancelled={fetchEvents} />}

      {/* Schedule meeting modal */}
      {showSchedule && <ScheduleMeetingModal onClose={() => setShowSchedule(false)} onCreated={fetchEvents} />}

      {/* Admin panel */}
      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Month Grid
// ═════════════════════════════════════════════════════════════════════════════
function MonthGrid({ focus, events, today, onSelectDay, onSelectEvent }) {
  const firstDay = startOfWeek(startOfMonth(focus));
  const days = Array.from({ length: 42 }, (_, i) => addDays(firstDay, i));
  const thisMonth = focus.getMonth();

  function eventsOn(d) {
    return events.filter(ev => {
      const s = new Date(ev.start);
      return isSameDay(s, d);
    });
  }

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, marginBottom: 1 }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 11, color: C.grayMid, padding: 4, fontWeight: 600 }}>{d}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
        {days.map((d, i) => {
          const isToday = isSameDay(d, today);
          const inMonth = d.getMonth() === thisMonth;
          const dayEvents = eventsOn(d);
          return (
            <div key={i} onClick={() => onSelectDay(d)}
              style={{
                background: C.white, borderRadius: 4, padding: 6, minHeight: 80,
                cursor: "pointer", opacity: inMonth ? 1 : 0.4,
                border: isToday ? `2px solid ${C.orange}` : `1px solid ${C.cardBorder}`,
              }}>
              <div style={{ fontSize: 12, fontWeight: isToday ? 800 : 400,
                color: isToday ? C.orange : C.white, marginBottom: 4 }}>{d.getDate()}</div>
              {dayEvents.slice(0, 3).map((ev, j) => (
                <div key={j} onClick={e => { e.stopPropagation(); onSelectEvent(ev); }}
                  style={{
                    fontSize: 10, padding: "2px 4px", borderRadius: 3, marginBottom: 2,
                    background: eventColor(ev), color: "#FFFFFF", whiteSpace: "nowrap",
                    overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer",
                  }}>{fmtTime(ev.start)} {ev.title}</div>
              ))}
              {dayEvents.length > 3 && (
                <div style={{ fontSize: 10, color: C.grayMid }}>+{dayEvents.length - 3} more</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Week Grid
// ═════════════════════════════════════════════════════════════════════════════
function WeekGrid({ focus, events, today, onSelectEvent }) {
  const weekStart = startOfWeek(focus);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  function eventsOnHour(d, h) {
    return events.filter(ev => {
      const s = new Date(ev.start);
      return isSameDay(s, d) && s.getHours() === h;
    });
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "60px repeat(7, 1fr)", fontSize: 11 }}>
      {/* Header */}
      <div />
      {days.map((d, i) => (
        <div key={i} style={{
          textAlign: "center", padding: 6, fontWeight: isSameDay(d, today) ? 800 : 400,
          color: isSameDay(d, today) ? C.orange : C.white, borderBottom: `1px solid ${C.cardBorder}`,
        }}>{fmtDateShort(d)}</div>
      ))}
      {/* Time rows */}
      {HOURS.map(h => (
        <div key={h} style={{ display: "contents" }}>
          <div style={{ padding: "4px 8px", color: C.grayMid, textAlign: "right", fontSize: 10,
            borderBottom: `1px solid ${C.cardBorder}` }}>
            {h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`}
          </div>
          {days.map((d, di) => {
            const evs = eventsOnHour(d, h);
            return (
              <div key={di} style={{ borderBottom: `1px solid ${C.cardBorder}`,
                borderLeft: `1px solid ${C.cardBorder}`, minHeight: 40, padding: 2 }}>
                {evs.map((ev, j) => (
                  <div key={j} onClick={() => onSelectEvent(ev)}
                    style={{
                      fontSize: 10, padding: "2px 4px", borderRadius: 3, marginBottom: 1,
                      background: eventColor(ev), color: "#FFFFFF", cursor: "pointer",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{ev.title}</div>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Day Grid
// ═════════════════════════════════════════════════════════════════════════════
function DayGrid({ focus, events, today, onSelectEvent }) {
  const dayEvents = events.filter(ev => isSameDay(new Date(ev.start), focus));

  return (
    <div>
      {HOURS.map(h => {
        const evs = dayEvents.filter(ev => new Date(ev.start).getHours() === h);
        return (
          <div key={h} style={{ display: "flex", borderBottom: `1px solid ${C.cardBorder}`, minHeight: 48 }}>
            <div style={{ width: 70, padding: "8px 8px", color: C.grayMid, fontSize: 11, textAlign: "right", flexShrink: 0 }}>
              {h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`}
            </div>
            <div style={{ flex: 1, padding: 4 }}>
              {evs.map((ev, j) => (
                <div key={j} onClick={() => onSelectEvent(ev)}
                  style={{
                    background: eventColor(ev), color: "#FFFFFF", borderRadius: 5,
                    padding: "6px 10px", marginBottom: 4, cursor: "pointer", fontSize: 13,
                  }}>
                  <div style={{ fontWeight: 700 }}>{ev.title}</div>
                  <div style={{ fontSize: 11, opacity: 0.8 }}>{fmtTime(ev.start)} - {fmtTime(ev.end)}</div>
                  {ev.meet_url && <div style={{ fontSize: 11 }}>Meet link available</div>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Event Detail Panel
// ═════════════════════════════════════════════════════════════════════════════
function EventDetail({ event, onClose, onCancelled }) {
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [toast, setToast] = useState(null);
  const start = event.start ? new Date(event.start) : null;
  const hasAttendees = (event.attendees || []).length > 0;
  const attendee = hasAttendees ? event.attendees[0] : {};
  const attendeeName = attendee.name || attendee.email || "attendee";

  async function cancelMeeting() {
    console.log("[CANCEL] Event object:", event);
    console.log("[CANCEL] Calling DELETE /calendar/events/" + event.id);
    setCancelling(true);
    const r = await api(`/calendar/events/${event.id}`, "DELETE");
    console.log("[CANCEL] Response:", r);
    setCancelling(false);
    if (r && r.cancelled) {
      setToast("Meeting cancelled and " + attendeeName + " notified");
      setTimeout(() => { onCancelled?.(); onClose(); }, 1500);
    } else {
      setToast(null);
      setShowCancel(false);
    }
  }

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0, width: 380, background: C.white,
      borderLeft: `1px solid ${C.cardBorder}`, padding: 20, overflowY: "auto", zIndex: 500,
      boxShadow: "-4px 0 20px rgba(0,0,0,0.08)",
    }}>
      {toast && (
        <div style={{ background: C.success, color: "#fff", padding: "8px 16px", borderRadius: 6,
          fontSize: 12, fontWeight: 600, marginBottom: 12 }}>{toast}</div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 800 }}>{event.title}</div>
        <button onClick={onClose} style={{ ...btn("ghost"), padding: "4px 8px" }}>X</button>
      </div>
      {start && <div style={{ fontSize: 13, color: C.orange, marginBottom: 12 }}>{fmtDateFull(start)} at {fmtTime(event.start)}</div>}
      {event.meet_url && (
        <a href={event.meet_url} target="_blank" rel="noreferrer"
          style={{ display: "block", ...btn("primary"), textAlign: "center", textDecoration: "none", marginBottom: 12 }}>
          Join Google Meet
        </a>
      )}
      {hasAttendees && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: C.grayMid, fontWeight: 700, marginBottom: 6, textTransform: "uppercase" }}>Attendees</div>
          {event.attendees.map((a, i) => (
            <div key={i} style={{ fontSize: 12, color: C.text, marginBottom: 4 }}>
              {a.name || a.email} <span style={{ color: C.grayMid }}>({a.status || "invited"})</span>
            </div>
          ))}
        </div>
      )}
      {event.description && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: C.grayMid, fontWeight: 700, marginBottom: 6, textTransform: "uppercase" }}>Description</div>
          <div style={{ fontSize: 12, color: C.grayMid, whiteSpace: "pre-wrap" }}>{event.description}</div>
        </div>
      )}
      {event.html_link && (
        <a href={event.html_link} target="_blank" rel="noreferrer"
          style={{ fontSize: 12, color: C.orange, display: "block", marginBottom: 16 }}>
          Open in Google Calendar
        </a>
      )}

      {/* Cancel meeting — only for events with attendees (bookings) */}
      {hasAttendees && !showCancel && (
        <button onClick={() => setShowCancel(true)}
          style={{ ...btn("ghost"), color: C.danger, borderColor: C.danger, width: "100%", marginTop: 12, fontSize: 12 }}>
          Cancel Meeting
        </button>
      )}
      {showCancel && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, padding: 12, marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#991B1B", marginBottom: 8 }}>Cancel this meeting?</div>
          <div style={{ fontSize: 12, color: "#7F1D1D", marginBottom: 12 }}>
            This will remove it from Google Calendar{attendee.email ? ` and notify ${attendee.email}` : ""}.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setShowCancel(false)} style={{ ...btn("ghost"), flex: 1, fontSize: 12 }}>Keep Meeting</button>
            <button onClick={cancelMeeting} disabled={cancelling}
              style={{ ...btn("primary"), flex: 1, fontSize: 12, background: C.danger }}>
              {cancelling ? "Cancelling..." : "Yes, Cancel"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Schedule Meeting Modal
// ═════════════════════════════════════════════════════════════════════════════
function ScheduleMeetingModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [title, setTitle] = useState("Evac & Transfer Readiness System");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [duration, setDuration] = useState(30);
  const [meet, setMeet] = useState(true);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);

  async function searchContact(q) {
    if (q.length < 2) { setSearchResults([]); return; }
    const r = await api(`/search?q=${encodeURIComponent(q)}&limit=5`);
    if (r?.contacts) setSearchResults(r.contacts);
  }

  function pickContact(c) {
    setName(c.full_name || "");
    setEmail(c.email_work || c.email_home || "");
    setCompany(c.org_name || "");
    setSearchResults([]);
  }

  async function save() {
    if (!name || !date || !time) return;
    setSaving(true);
    setError(null);
    const startISO = `${date}T${time}:00`;
    const endDate = new Date(startISO);
    endDate.setMinutes(endDate.getMinutes() + duration);
    const endISO = endDate.toISOString();

    const r = await api("/calendar/events", "POST", {
      title: `${title} - ${name} (${company || "N/A"})`,
      start: startISO,
      end: endISO,
      attendee_email: email || undefined,
      attendee_name: name,
      description: `Company: ${company}\nNotes: ${notes}`,
      meet,
    });
    setSaving(false);
    if (r) {
      setResult(r);
      onCreated();
    } else {
      setError("Failed to create event. Check Railway logs.");
    }
  }

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.white, borderRadius: 10, padding: 24, width: 520, maxWidth: "95vw",
        maxHeight: "90vh", overflowY: "auto", border: `1px solid ${C.cardBorder}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>Schedule Meeting</div>
          <button onClick={onClose} style={{ ...btn("ghost"), padding: "4px 8px" }}>X</button>
        </div>

        {result ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <div style={{ fontSize: 24, marginBottom: 12 }}>Meeting Scheduled</div>
            {result.meet_url && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: C.grayMid, marginBottom: 4 }}>Google Meet Link</div>
                <a href={result.meet_url} target="_blank" rel="noreferrer" style={{ color: C.orange, fontSize: 14, wordBreak: "break-all" }}>{result.meet_url}</a>
              </div>
            )}
            {result.html_link && (
              <a href={result.html_link} target="_blank" rel="noreferrer"
                style={{ ...btn("primary"), display: "inline-block", textDecoration: "none", marginTop: 8 }}>
                View in Google Calendar
              </a>
            )}
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.grayMid, textTransform: "uppercase" }}>Contact Name</label>
              <input value={name} onChange={e => { setName(e.target.value); searchContact(e.target.value); }}
                placeholder="Type to search CRM..." style={input} />
              {searchResults.length > 0 && (
                <div style={{ background: "#F8FAFC", border: `1px solid ${C.cardBorder}`, borderRadius: 5, marginTop: 4 }}>
                  {searchResults.map(c => (
                    <div key={c.id} onClick={() => pickContact(c)}
                      style={{ padding: "8px 10px", cursor: "pointer", fontSize: 12, borderBottom: `1px solid ${C.cardBorder}` }}>
                      {c.full_name} {c.email_work ? `(${c.email_work})` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: C.grayMid, textTransform: "uppercase" }}>Email</label>
                <input value={email} onChange={e => setEmail(e.target.value)} style={input} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.grayMid, textTransform: "uppercase" }}>Company</label>
                <input value={company} onChange={e => setCompany(e.target.value)} style={input} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.grayMid, textTransform: "uppercase" }}>Meeting Type</label>
              <select value={title} onChange={e => setTitle(e.target.value)} style={select}>
                <option>Evac & Transfer Readiness System</option>
                <option>72-Hour Readiness Assessment</option>
                <option>Product Demo</option>
                <option>Follow Up</option>
                <option>General Meeting</option>
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: C.grayMid, textTransform: "uppercase" }}>Date</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} style={input} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.grayMid, textTransform: "uppercase" }}>Time</label>
                <input type="time" value={time} onChange={e => setTime(e.target.value)} style={input} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.grayMid, textTransform: "uppercase" }}>Duration</label>
                <select value={duration} onChange={e => setDuration(Number(e.target.value))} style={select}>
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: C.grayMid, textTransform: "uppercase" }}>Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                rows={3} style={{ ...input, resize: "vertical" }} />
            </div>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: C.grayMid, marginBottom: 16 }}>
              <input type="checkbox" checked={meet} onChange={e => setMeet(e.target.checked)} />
              Create Google Meet link
            </label>
            {error && <div style={{ color: C.danger, fontSize: 12, marginBottom: 8 }}>{error}</div>}
            <button onClick={save} disabled={saving || !name || !date}
              style={{ ...btn("primary"), width: "100%", opacity: (name && date) ? 1 : 0.5 }}>
              {saving ? "Creating..." : "Schedule Meeting"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Admin Panel (slides in from right) — full availability settings
// ═════════════════════════════════════════════════════════════════════════════
function AdminPanel({ onClose }) {
  const [tab, setTab] = useState("settings"); // settings | types | bookings
  const [settings, setSettings] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [types, setTypes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState(null); // {msg, type: "success"|"error"}
  const BOOKING_URL = "https://build-black-tau.vercel.app/#booking";

  // Backend stores hours as floats (12.5 = 12:30pm). Convert for display.
  const floatToTime = (f) => {
    const h = Math.floor(f); const m = Math.round((f - h) * 60);
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
  };
  const timeToFloat = (t) => {
    const [h, m] = (t || "08:00").split(":").map(Number);
    return h + m / 60;
  };

  // Backend uses 0-indexed days (Mon=0..Sun=6). UI maps: Mon=0,Tue=1,...
  useEffect(() => {
    api("/booking/settings").then(r => {
      if (r) {
        // Convert floats to time strings for the input
        r._startTime = floatToTime(r.start_hour || 8);
        r._endTime = floatToTime(r.end_hour || 17);
        setSettings(r);
      }
    });
    api("/booking/types").then(r => { setTypes(Array.isArray(r) ? r : []); setLoading(false); });
    api("/booking/upcoming?admin_email=drew@outpostprep.com").then(r => setBookings(Array.isArray(r) ? r : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function showToast(msg, type) {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  async function saveSettings() {
    if (!settings) return;
    setSaveState("saving");
    // Build payload matching backend field names
    const payload = {
      available_days: settings.available_days,
      start_hour: timeToFloat(settings._startTime),
      end_hour: timeToFloat(settings._endTime),
      buffer_minutes: settings.buffer_minutes,
      max_advance_days: settings.max_advance_days,
    };
    console.log("Saving booking settings:", payload);
    // POST with admin_email query param (required by backend)
    const r = await api(
      "/booking/settings?admin_email=drew@outpostprep.com",
      "POST",
      payload
    );
    console.log("Save response:", r);
    if (r && !r.detail) {
      setSaveState("saved");
      showToast("Settings saved successfully", "success");
      // Update local state with response
      r._startTime = floatToTime(r.start_hour);
      r._endTime = floatToTime(r.end_hour);
      setSettings(r);
      setTimeout(() => setSaveState("idle"), 2000);
    } else {
      setSaveState("error");
      showToast("Failed to save — try again", "error");
      setTimeout(() => setSaveState("idle"), 2000);
    }
  }

  function copyLink() {
    navigator.clipboard.writeText(BOOKING_URL).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  const saveBtnLabel = { idle: "Save Settings", saving: "Saving...", saved: "Saved \u2713", error: "Save Settings" }[saveState];
  const saveBtnStyle = saveState === "saved"
    ? { ...btn("primary"), marginTop: 8, width: "100%", background: C.success }
    : { ...btn("primary"), marginTop: 8, width: "100%" };

  const statusColor = { confirmed: C.success, cancelled: C.danger, rescheduled: C.warning };
  const tabStyle = (t) => ({
    ...btn(tab === t ? "primary" : "ghost"),
    fontSize: 11, padding: "5px 12px", flex: 1, textAlign: "center",
  });
  const label = { fontSize: 11, color: C.grayMid, fontWeight: 700, textTransform: "uppercase", marginBottom: 6, display: "block" };

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0, width: 440, background: C.white,
      borderLeft: `1px solid ${C.cardBorder}`, padding: 20, overflowY: "auto", zIndex: 600,
      boxShadow: "-4px 0 20px rgba(0,0,0,0.08)",
    }}>
      {/* Toast notification */}
      {toast && (
        <div style={{
          position: "fixed", top: 16, right: 460, zIndex: 700,
          background: toast.type === "success" ? C.success : C.danger,
          color: "#FFFFFF", padding: "10px 20px", borderRadius: 6,
          fontSize: 13, fontWeight: 600, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          animation: "fadeIn 0.2s ease",
        }}>
          {toast.msg}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Availability Settings</div>
        <button onClick={onClose} style={{ ...btn("ghost"), padding: "4px 8px" }}>X</button>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        <button style={tabStyle("settings")} onClick={() => setTab("settings")}>Settings</button>
        <button style={tabStyle("types")} onClick={() => setTab("types")}>Appt Types</button>
        <button style={tabStyle("bookings")} onClick={() => setTab("bookings")}>Bookings</button>
      </div>

      {/* ── SETTINGS TAB ── */}
      {tab === "settings" && (
        <div>
          {!settings ? <div style={{ color: C.grayMid, fontSize: 12 }}>Loading settings...</div> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <span style={label}>Available Days</span>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => {
                    const days = settings.available_days || [0,1,2,3,4];
                    const active = days.includes(i);
                    return (
                      <button key={d} onClick={() => {
                        const nd = active ? days.filter(x => x !== i) : [...days, i].sort();
                        setSettings(s => ({ ...s, available_days: nd }));
                      }}
                        style={{ ...btn(active ? "primary" : "ghost"), fontSize: 11, padding: "4px 10px" }}>{d}</button>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <span style={label}>Start Time</span>
                  <input type="time" value={settings._startTime || "08:00"}
                    onChange={e => setSettings(s => ({ ...s, _startTime: e.target.value }))} style={input} />
                </div>
                <div>
                  <span style={label}>End Time</span>
                  <input type="time" value={settings._endTime || "17:00"}
                    onChange={e => setSettings(s => ({ ...s, _endTime: e.target.value }))} style={input} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <span style={label}>Buffer (min)</span>
                  <input type="number" value={settings.buffer_minutes ?? 15}
                    onChange={e => setSettings(s => ({ ...s, buffer_minutes: Number(e.target.value) }))} style={input} />
                </div>
                <div>
                  <span style={label}>Booking Window (days)</span>
                  <input type="number" value={settings.max_advance_days ?? 21}
                    onChange={e => setSettings(s => ({ ...s, max_advance_days: Number(e.target.value) }))} style={input} />
                </div>
              </div>
              <button onClick={saveSettings} disabled={saveState === "saving"}
                style={saveBtnStyle}>
                {saveBtnLabel}
              </button>
            </div>
          )}

          {/* Public Booking URL */}
          <div style={{ borderTop: `1px solid ${C.cardBorder}`, marginTop: 24, paddingTop: 16 }}>
            <span style={label}>Public Booking URL</span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <div style={{ flex: 1, fontSize: 12, color: C.orange, wordBreak: "break-all", background: "#FFF7ED", padding: "8px 10px", borderRadius: 5, border: `1px solid ${C.cardBorder}` }}>
                {BOOKING_URL}
              </div>
              <button onClick={copyLink} style={{ ...btn("primary"), fontSize: 11, padding: "8px 12px", whiteSpace: "nowrap" }}>
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── APPOINTMENT TYPES TAB ── */}
      {tab === "types" && (
        <div>
          {loading ? <div style={{ color: C.grayMid, fontSize: 12 }}>Loading...</div> :
            types.length === 0 ? <div style={{ color: C.grayMid, fontSize: 12 }}>No appointment types configured.</div> :
            types.map(t => (
              <div key={t.id || t.slug} style={{
                background: "#F8FAFC", borderRadius: 6, padding: 12, marginBottom: 8,
                border: `1px solid ${C.cardBorder}`,
              }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{t.name}</div>
                <div style={{ fontSize: 11, color: C.grayMid, marginTop: 2 }}>
                  {t.duration_minutes} min &mdash; /{t.slug}
                </div>
                {t.description && <div style={{ fontSize: 11, color: C.grayMid, marginTop: 4 }}>{t.description}</div>}
                <div style={{ marginTop: 8 }}>
                  <button onClick={() => {
                    const url = `${BOOKING_URL}/${t.slug}`;
                    navigator.clipboard.writeText(url);
                  }} style={{ ...btn("ghost"), fontSize: 10, padding: "3px 8px" }}>
                    Copy Link
                  </button>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {/* ── BOOKINGS TAB ── */}
      {tab === "bookings" && (
        <div>
          <div style={{ fontSize: 12, color: C.grayMid, marginBottom: 12, fontWeight: 700, textTransform: "uppercase" }}>
            Upcoming ({bookings.length})
          </div>
          {bookings.length === 0 ? <div style={{ color: C.grayMid, fontSize: 12 }}>No upcoming bookings.</div> :
            bookings.map(b => {
              let ts = b.start_time || "";
              try { ts = new Date(b.start_time).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch {}
              return (
                <div key={b.id} style={{
                  background: "#F8FAFC", borderRadius: 6, padding: 10, marginBottom: 8,
                  borderLeft: `3px solid ${statusColor[b.status] || C.grayMid}`,
                  border: `1px solid ${C.cardBorder}`,
                }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{b.name}</div>
                  <div style={{ fontSize: 11, color: C.grayMid }}>{b.company || ""} {b.email}</div>
                  <div style={{ fontSize: 12, color: C.orange, marginTop: 4 }}>{ts}</div>
                  <div style={{ fontSize: 10, color: statusColor[b.status] || C.grayMid, fontWeight: 700, textTransform: "uppercase", marginTop: 2 }}>{b.status}</div>
                  {b.status !== "cancelled" && (
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      <button onClick={async () => {
                        if (!window.confirm(`Cancel booking with ${b.name}?`)) return;
                        if (b.gcal_event_id) {
                          await api(`/calendar/events/${b.gcal_event_id}`, "DELETE");
                        } else {
                          await api(`/bookings/${b.id}`, "PATCH", { status: "cancelled" });
                        }
                        setBookings(prev => prev.map(x => x.id === b.id ? { ...x, status: "cancelled" } : x));
                      }}
                        style={{ ...btn("ghost"), fontSize: 10, padding: "3px 8px", color: C.danger, borderColor: C.danger }}>
                        Cancel
                      </button>
                      <button onClick={() => {
                        const url = `https://build-black-tau.vercel.app/#booking?reschedule&booking_id=${b.id}`;
                        navigator.clipboard.writeText(url);
                      }}
                        style={{ ...btn("ghost"), fontSize: 10, padding: "3px 8px" }}>
                        Reschedule Link
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          }
        </div>
      )}
    </div>
  );
}
